/**
 * CookieConsent — GDPR/CCPA-friendly bottom banner + preferences dialog.
 *
 * Behaviour:
 *   - Mounts once at the App level; renders nothing if a consent
 *     record is already in localStorage.
 *   - Otherwise a soft-slide banner appears at the bottom of the
 *     viewport with Accept-all / Reject-non-essential / Customise.
 *   - Customise opens a Radix Dialog with toggles for Analytics and
 *     Marketing.  Essential cookies are always on and disabled.
 *   - Persists the choice as JSON in `localStorage[STORAGE_KEY]`.
 *
 * We also broadcast a `window.dispatchEvent('fda:cookie-consent', {detail})`
 * so any downstream analytics / ad code can gate itself.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Cookie, Settings2, Check, ShieldCheck, BarChart3, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AuthContext } from '../App';

const STORAGE_KEY = 'fda_cookie_consent_v1';

const readConsent = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeConsent = (consent) => {
  const payload = { ...consent, timestamp: new Date().toISOString(), version: 1 };
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (_) { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('fda:cookie-consent', { detail: payload }));
  } catch (_) { /* ignore */ }
  return payload;
};

const CookieConsent = () => {
  const { user } = useContext(AuthContext) || {};
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // Mount decision — show only if no record yet, soft-delay so we
  // don't fight with the welcome video / other splashes.
  useEffect(() => {
    if (readConsent()) return;
    // Authenticated users have already accepted our ToS at signup, which
    // covers essential cookies — auto-record an essential-only consent so
    // the banner doesn't cover lesson controls (Post/Submit buttons).
    // They can still change preferences from the footer/settings later.
    if (user) {
      writeConsent({ essential: true, analytics: false, marketing: false });
      return;
    }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const acceptAll = () => {
    writeConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectNonEssential = () => {
    writeConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const savePreferences = () => {
    writeConsent({ essential: true, analytics, marketing });
    setPrefsOpen(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* ── Bottom banner ─────────────────────────────────── */}
      <div
        className="fixed inset-x-0 bottom-0 z-[45] p-3 sm:p-4 pointer-events-none animate-in slide-in-from-bottom-4 fade-in duration-500"
        data-testid="cookie-banner"
      >
        <div className="pointer-events-auto max-w-5xl mx-auto rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-2xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4">
          <div className="hidden sm:flex h-11 w-11 rounded-xl bg-primary/15 text-primary items-center justify-center flex-shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-extrabold tracking-tight text-foreground">
              We value your privacy
            </div>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
              We use essential cookies to run the site plus optional cookies for analytics and marketing.
              You can accept all, reject non-essential, or customise your choices. Read our{' '}
              <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">privacy policy</a>{' '}
              for details.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPrefsOpen(true)}
              className="rounded-full font-semibold text-muted-foreground"
              data-testid="cookie-customize-btn"
            >
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Customise
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={rejectNonEssential}
              className="rounded-full font-semibold"
              data-testid="cookie-reject-btn"
            >
              Reject non-essential
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              className="rounded-full font-extrabold"
              data-testid="cookie-accept-btn"
            >
              <Check className="w-3.5 h-3.5 mr-1.5" /> Accept all
            </Button>
          </div>
        </div>
      </div>

      {/* ── Customise dialog ──────────────────────────────── */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg" data-testid="cookie-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-extrabold tracking-tight">
              Cookie preferences
            </DialogTitle>
            <DialogDescription>
              Choose which categories of cookies you allow. You can change this at any time from the site footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            {/* Essential — always on */}
            <div className="rounded-xl border border-border bg-muted/30 p-4 flex items-start gap-3" data-testid="cookie-cat-essential">
              <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-heading font-extrabold text-sm">Essential</div>
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary">Always on</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Auth session, security, load balancing, and remembering your preferences. Required to run the site.
                </p>
              </div>
            </div>

            {/* Analytics */}
            <div className="rounded-xl border border-border p-4 flex items-start gap-3" data-testid="cookie-cat-analytics">
              <div className="h-9 w-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-heading font-extrabold text-sm">Analytics</div>
                  <Switch
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                    data-testid="cookie-toggle-analytics"
                    aria-label="Toggle analytics cookies"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Anonymous usage stats so we know which lessons and tools are getting used. Helps us prioritise what to build next.
                </p>
              </div>
            </div>

            {/* Marketing */}
            <div className="rounded-xl border border-border p-4 flex items-start gap-3" data-testid="cookie-cat-marketing">
              <div className="h-9 w-9 rounded-lg bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
                <Megaphone className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-heading font-extrabold text-sm">Marketing</div>
                  <Switch
                    checked={marketing}
                    onCheckedChange={setMarketing}
                    data-testid="cookie-toggle-marketing"
                    aria-label="Toggle marketing cookies"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Personalise the ads you see on other sites so we&apos;re not wasting your time (or ours) with irrelevant ones.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={rejectNonEssential} className="rounded-full font-semibold text-muted-foreground" data-testid="cookie-prefs-reject-btn">
              Reject non-essential
            </Button>
            <Button size="sm" onClick={savePreferences} className="rounded-full font-extrabold" data-testid="cookie-prefs-save-btn">
              Save preferences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Utility hook — read the current consent record.  Any analytics / ad
 * bootstrap code can check `useCookieConsent().analytics` before firing.
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(() => readConsent());
  useEffect(() => {
    const onEvent = (e) => setConsent(e.detail);
    window.addEventListener('fda:cookie-consent', onEvent);
    return () => window.removeEventListener('fda:cookie-consent', onEvent);
  }, []);
  return consent || { essential: true, analytics: false, marketing: false };
};

export default CookieConsent;
